<template>
    <div class="marketing-site-page">
        <div class="header">
            <h2>{{ $options.TITLE }}</h2>
            <ds-filled-button
                class="new-marketing-site"
                @click="createTemplate"
            >
                {{ $options.CREATE }}
            </ds-filled-button>
        </div>

        <div class="filter">
            <ds-input-field
                id="search-entry"
                v-model="search"
                class="input-search"
                :label="$options.SEARCH"
                type="search"
                @input="updateSearch"
            />

            <marketing-site-filter
                class="filter-type"
                :filter.sync="filter"
            />
        </div>

        <choose-site-name-modal
            :is-open="chooseNameModalOptions.isOpen"
            :initial-name="chooseNameModalOptions.initialName"
            :title="chooseNameModalOptions.title"
            :helper-text="chooseNameModalOptions.helperText"
            :name-input-label="chooseNameModalOptions.nameInputLabel"
            :validate="chooseNameModalOptions.validate"
            @close="handleCancelChooseName"
            @rename="handleChooseName"
        />
        <div class="site-table-wrapper">
            <ds-table-grid
                key="key"
                :class="{ 'site-table': !loading, 'site-table-placeholder': loading }"
                :data="loading ? [] : templatesData"
                :placeholder-height="75"
                :click-row="(row,event) => openSite(row,event)"
                :sort-field="$options.INITIAL_SORT_FIELD"
                :sort-ascending="$options.INITIAL_SORT_ASCENDING"
                @sort="sortTemplates"
            >
                <ds-table-grid-column
                    :label="$t('landingPages.grid.columns.name', { defaultMessage: 'Landing page name' })"
                    prop="title"
                    sortable
                >
                    <template slot-scope="props">
                        {{ titleText(props.title) }}
                    </template>
                </ds-table-grid-column>

                <ds-table-grid-column
                    :label="$t('landingPages.grid.columns.status', { defaultMessage: 'Status' })"
                    width="100"
                    prop="status"
                    sortable
                >
                    <template #default="template">
                        <div>
                            <p class="site-status" :class="statusClassName(template)">
                                {{ statusLabel(template) }}
                            </p>
                        </div>
                    </template>
                </ds-table-grid-column>

                <ds-table-grid-column
                    :label="$t('landingPages.grid.columns.updatedDate', { defaultMessage: 'Last edited' })"
                    prop="updateTime"
                    sortable
                />

                <ds-table-grid-column
                    :label="$t('landingPages.grid.columns.dateCreated', { defaultMessage: 'Date created' })"
                    prop="createTime"
                    sortable
                />

                <ds-table-grid-column
                    :label="$t('landingPages.grid.columns.pageViews', { defaultMessage: 'Page views' })"
                    prop="pageViews"
                    numeric
                >
                    <template #default="template">
                        {{ template.pageViews != null ? template.pageViews : '-' }}
                    </template>
                </ds-table-grid-column>

                <ds-table-grid-column
                    :label="$t('landingPages.grid.columns.uniqueVisitors', { defaultMessage: 'Unique visitors' })"
                    prop="uniqueVisitors"
                    numeric
                >
                    <template #default="template">
                        {{ template.uniqueVisitors != null ? template.uniqueVisitors : '-' }}
                    </template>
                </ds-table-grid-column>

                <ds-table-grid-column
                    :label="$t('landingPages.grid.columns.conversionRate', { defaultMessage: 'Conversion rate' })"
                    prop="conversionRate"
                    numeric
                >
                    <template #default="template">
                        {{ template.conversionRate != null ? `${template.conversionRate}%` : '-' }}
                    </template>
                </ds-table-grid-column>

                <ds-table-grid-column>
                    <template #default="template">
                        <div class="options-cell">
                            <marketing-site-options
                                :key="template.id"
                                :site-status="template.status"
                                :template-id="template.id"
                                @open-site-preview="openSitePreview(template.alias)"
                                @copy-site-link="copySiteLink(template.alias)"
                                @rename="renameSite(template)"
                                @copy="copySite(template.id)"
                                @delete="deleteSite(template.id)"
                                @edit="editTemplate(template.id)"
                                @preview="$emit('preview', template.id)"
                            />
                        </div>
                    </template>
                </ds-table-grid-column>
            </ds-table-grid>

            <div v-if="loading" class="placeholder">
                <ds-placeholder
                    :rows="[
                        { height: '0', boxes: [1] },
                        ...Array(limit).fill({ height: '0.3125rem', boxes: [1, 1, 1, 1, 1, 1, 1, 1] }),
                        { height: '0', boxes: [1] }
                    ]"
                />
            </div>
        </div>
        <footer class="pagination-footer">
            <pagination-control
                :total-count="totalCount"
                :limit="limit"
                :offset="offset"
                @page="updatePage"
            />

            <div class="page-size">
                <ds-select-field
                    v-model="limit"
                    class="limit-select"
                    dense
                    bind-value-only
                    :options="pageSizes"
                />
                Per Page
            </div>
        </footer>
    </div>
</template>

<script>
import ChooseSiteNameModal from './ChooseSiteNameModal';
import MarketingSiteFilter from './MarketingSiteFilter';
import MarketingSiteOptions from './MarketingSiteOptions';
import PaginationControl from '../shared/PaginationControl.vue';
import axios from 'axios';
import {
    buildMarketingSiteUrl,
    cloneSiteFromSite,
} from '../util/marketing/site.utils';

import {
    EDITOR_ROUTE_URL,
    GALLERY_ROUTE_URL,
    LIST_SOURCE_URL,
    SITE_STATUS,
} from '../util/marketing/unlayer.constants';

import {
    getTemplateGroup, deleteTemplateGroup, patchTemplateGroup, getCustomDomainsForTenant,
} from '../util/marketing/marketingSiteEditorApi';
import debounce from 'lodash.debounce';

export const TITLE = 'Landing Pages';
export const CREATE = 'New Landing Page';
export const SEARCH = 'Search landing pages';
export const ERROR_SITE_PREVIEW = 'There was an error opening the preview';
export const ERROR_COPYING_URL = 'There was a problem copying this URL';
export const ERROR_COPYING_SITE = 'There was an error copying this site';
export const COPY_CLIPBOARD = 'Your url was copied to your clipboard';
export const RENAME_MODAL_TITLE = 'Name your landing page';
export const NAME_INPUT_LABEL = 'Page Name';
export const HELPER_TEXT = 'This will appear in the browser title bar and landing page url.';
export const DELETE_TEMPLATE_GROUP = 'Delete Landing Page';
export const DELETE_CONFIRM = 'Are you sure you want to delete this landing page?';
export const DELETE_ERROR = 'There was a problem deleting';
export const DEFAULT_LIMIT = 25;
export const INITIAL_SORT_FIELD = 'updateTime';
export const INITIAL_SORT_ASCENDING = false;

export default {
    TITLE,
    SEARCH,
    CREATE,
    DEFAULT_LIMIT,
    INITIAL_SORT_FIELD,
    INITIAL_SORT_ASCENDING,
    components: {
        ChooseSiteNameModal,
        PaginationControl,
        MarketingSiteFilter,
        MarketingSiteOptions,
    },
    props: {
        templates: {
            type: String,
        },
        nextPage: {
            type: String,
        },
        pageSize: {
            type: String,
        },
        pages: {
            type: String,
        },
        tenantId: {
            type: String,
        },
        currentEnv: {
            type: String,
        },
        count: {
            type: String,
        },
    },

    data() {
        return {
            chooseNameModalOptions: {},
            pageSizes: [
                { label: '10', value: 10 },
                { label: '25', value: 25 },
            ],
            templatesData: JSON.parse(this.templates),
            limit: Number(this.pageSize) > DEFAULT_LIMIT ? Number(this.pageSize) : DEFAULT_LIMIT,
            currentPageSize: Number(this.pageSize),
            pageToken: Number(this.nextPage),
            totalPages: Number(this.pages),
            filter: {},
            search: '',
            sortField: 'updateTime',
            sortAscending: false,
            loading: false,
            debounceDelay: 500,
            totalCount: 0,
            previousOffset: 0,
            customDomain: {},
            isCustomDomainLoaded: false,
        };
    },

    mounted() {
        this.loadCustomDomain(this.tenantId);
    },

    created() {
        this.updateSearch = debounce(this.updateSearch, this.debounceDelay);
        this.totalCount = Number(this.count);
    },

    watch: {
        limit() {
            this.reload();
        },

        filter() {
            this.reload();
        },
    },

    computed: {
        environment() {
            return { production: 'production', integration: 'integration', staging: 'staging' }[(this.currentEnv || '').toLowerCase()] || 'integration';
        },

        offset() {
            if (this.loading) {
                return this.previousOffset;
            }

            let response = (this.pageToken * this.limit) - this.limit;

            if (this.pageToken < 1) {
                response = (this.totalPages * this.limit) - this.limit;
            }

            return response;
        },
    },
    methods: {
        async openSite(site, event) {
            if (['td', 'p'].includes(event?.target?.localName)
                    || ['numeric-cell', 'options-cell', 'cell'].includes(event?.target?.className)
            ) {
                try {
                    this.editTemplate(site?.id);
                } catch (e) {
                    this.$error({ message: this.$t('landingPageLoadError'), bottom: true });
                }
            }
        },

        editTemplate(id) {
            window.location.href = `${EDITOR_ROUTE_URL}/${id}`;
        },

        createTemplate() {
            window.location.href = GALLERY_ROUTE_URL;
        },

        updatePage({ offset }) {
            this.previousOffset = this.offset;
            this.pageToken = ((offset + this.limit) / this.limit) - 1;
            this.load();
        },

        getSortBy(sortField, sortAscending) {
            switch (sortField) {
            case 'title':
                return `TITLE_${ this.getSortByDirection(sortAscending) }`;
            case 'status':
                return `STATUS_${ this.getSortByDirection(sortAscending) }`;
            case 'updateTime':
                return `UPDATETIME_${ this.getSortByDirection(sortAscending) }`;
            case 'createTime':
                return `CREATEDTIME_${ this.getSortByDirection(sortAscending) }`;
            default:
                return 'UPDATETIME_DESC';
            }
        },

        getSortByDirection(sortAscending) {
            return sortAscending ? 'ASC' : 'DESC';
        },

        async load() {
            const listQueryString = new URLSearchParams({
                pageToken: this.pageToken,
                pageSize: this.limit,
                search: this.search,
                filter: this.filter.value,
                sortBy: this.getSortBy(this.sortField, this.sortAscending),
            });

            this.loading = true;

            const listResponse = await axios.get(`${LIST_SOURCE_URL}?${listQueryString}`);

            this.loading = false;

            if (listResponse?.data?.templateGroupList) {
                const data = listResponse?.data?.templateGroupList;

                this.templatesData = data?.templateGroups;
                this.pageToken = Number(data?.nextPage);
                this.currentPageSize = Number(data?.pageSize);
                this.totalPages = Number(data?.pages);
                this.totalCount = Number(data?.totalSize);
            }
        },

        async loadCustomDomain(tenantId) {
            const data = await getCustomDomainsForTenant(tenantId);

            this.isCustomDomainLoaded = true;

            if (data?.setupComplete) {
                this.customDomain = {
                    dkimName: data.domainName,
                    domainName: data.domainName,
                    verified: true,
                };
            }
        },

        reload() {
            this.previousOffset = this.offset;
            this.pageToken = 0;
            this.load();
        },

        updateSearch() {
            this.reload();
        },

        async openSitePreview(alias) {
            const linkUrl = buildMarketingSiteUrl(this.environment, this.tenantId, alias, this.customDomain);

            if (!linkUrl) {
                this.$toast({
                    message: ERROR_SITE_PREVIEW,
                });
            } else {
                window.open(linkUrl, '_blank', 'noopener,noreferrer');
            }
        },

        async copySiteLink(alias) {
            const linkUrl = buildMarketingSiteUrl(this.environment, this.tenantId, alias, this.customDomain);

            if (!linkUrl) {
                this.$error({ message: ERROR_COPYING_URL, bottom: true });
            } else {
                await this.$copyText(linkUrl);
                this.$toast({ message: COPY_CLIPBOARD });
            }
        },

        async deleteSite(id) {
            try {
                await this.$confirm({
                    title: DELETE_TEMPLATE_GROUP,
                    message: DELETE_CONFIRM,
                    confirmLabel: 'OK',
                    cancelLabel: 'Cancel',
                    destructive: true,
                });
            } catch (error) {
                return;
            }

            try {
                await deleteTemplateGroup(id);
                this.reload();
            } catch (error) {
                this.$error({ message: DELETE_ERROR, bottom: true });
            }
        },

        async copySite(id) {
            const response = await getTemplateGroup(id);

            if (response?.data?.templateGroupDto) {
                const sourceTemplate = response.data.templateGroupDto;
                const { title: oldName } = sourceTemplate;
                const name = await this.chooseName(`Copy of ${ oldName }`);

                try {
                    if (name) {
                        await cloneSiteFromSite(sourceTemplate, name, this.tenantId, this.environment);

                        this.closeChooseNameModal();
                        this.reload();
                        this.selectedTemplate = {};
                    }
                } catch (err) {
                    this.$error({ message: ERROR_COPYING_SITE, bottom: true });
                } finally {
                    this.chooseNameModalOptions = {};
                }
            }

            return id;
        },

        async renameSite(template) {
            const name = await this.chooseName(template?.title.replaceAll('&apos;', "'"));

            if (!name) {
                return template;
            }
            await patchTemplateGroup(template.id, { title: name });

            this.reload();
            this.closeChooseNameModal();

            return template;
        },

        handleCancelChooseName() {
            if (this.chooseNameModalOptions?.resolve) {
                this.chooseNameModalOptions?.resolve?.call(this);
                this.selectedTemplate = {};
                this.chooseNameModalOptions = {};
            }
        },

        handleChooseName(name) {
            this.chooseNameModalOptions?.resolve?.call(this, name);
        },

        closeChooseNameModal() {
            this.chooseNameModalOptions = {};
        },

        chooseName(initialName, title = RENAME_MODAL_TITLE, nameInputLabel = NAME_INPUT_LABEL, helperText = HELPER_TEXT, validate = true) {
            this.chooseNameModalOptions?.resolve?.call();

            return new Promise((resolve) => {
                this.chooseNameModalOptions = {
                    isOpen: true,
                    initialName,
                    resolve,
                    title,
                    nameInputLabel,
                    helperText,
                    validate,
                };
            });
        },

        statusLabel({ status = SITE_STATUS.DRAFT }) {
            const statusMapping = {
                ACTIVE: 'Published',
                ACTIVE_CHANGES: 'Unpublished changes',
                DRAFT: 'Draft',
            };

            return this.$t(`landingPages.statusChip.${status}`, { defaultMessage: statusMapping[status] });
        },

        statusClassName({ status = SITE_STATUS.DRAFT }) {
            return status.toLowerCase();
        },

        sortTemplates(options) {
            this.sortField = options?.field ?? this.sortField;
            this.sortAscending = options?.ascending ?? this.sortAscending;
            this.reload();
        },

        titleText(title) {
            return title?.replaceAll('&apos;', "'");
        },
    },
};
</script>

<style lang="scss" scoped>
    div.marketing-site-page {
        min-height: 100%;
        justify-content: space-between;
    }
    div.marketing-site-page div.header {
        display: flex;
        justify-content: space-between;
        padding-bottom: 2rem;
    }
    div.marketing-site-page div.filter {
        display: flex;
        justify-content: space-between;
        padding-bottom: 0.75rem;
    }

    div.marketing-site-page div.filter div.input-search {
        flex-direction: unset;
        width: 350px;
    }

    .loading-spinner {
        width: 100%;
        text-align: center;
        margin: 200px 0;
        z-index: 10;
        position: absolute;
        justify-content: center;
        align-items: center;
    }

    footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        box-shadow: inset 0 1px 0 $color-gray-200;

        @media($small) {
            grid-template-columns: 100%;
            justify-content: center;
            text-align: center;
        }

        .page-size {
            display: grid;
            grid-template-columns: px-to-rem(80) auto;
            align-items: center;
            grid-gap: $spacing-100;
            @media($small) { display: none }
        }
    }

    .site-name {
        margin: 0;
    }

    .site-status {
        margin: px-to-rem(4px) 0 0;
        padding: 0;
        word-break: keep-all;
        font-size: $font-size-xs;
    }

    .site-status.active {
        color: $color-green-700;
    }

    .site-status.draft {
        color: $color-gray-800;
    }

    .site-status.active_changes {
        color: $color-orange-900;
    }

    .site-table-wrapper {
        height: calc(100vh - 345px);
        overflow: hidden
    }

    .site-table-wrapper.loading .site-table, .site-table-wrapper.loading .site-table-placeholder {
        height: auto;
    }

    .site-table-placeholder {
        height: px-to-rem(54px);
        overflow-y: auto;
    }

    .site-table {
        height: 100%;
        overflow-y: auto;
    }

    .pagination-footer {
        bottom: 18px;
        display: grid;
        grid-template-columns: auto auto;
        width: 100%;
        padding-right: 3.5rem;
        align-items: center;
        justify-content: space-between;
        border-top: 1px solid $color-gray-200;
        background-color: $color-paper;

        @media($small) {
            grid-template-columns: 100%;
            justify-content: center;
            text-align: center;
        }
    }

    .page-size {
        font-size: $font-size-xs;
        color: $color-text-subtle;
        line-height: $font-size-md;
        display: grid;
        grid-template-columns: px-to-rem(80) auto;
        align-items: center;
        grid-gap: $spacing-100;
        justify-content: space-between;
        text-align: center;

        @media($small) { display: none }
    }

    .page-size .limit-select {
        padding-top: 1rem;
    }
</style>

<style lang="scss">
    .site-table, .site-table-placeholder {
        @media(max-width: 1120px) {
            overflow-x: auto;
        }
        .table-wrapper {
            //I was having an issue where the default .table-wrapper style was truncating overflow and chopping
            // my hover menus
            overflow: visible;

            table {
                table-layout: auto;
            }

            thead {
                position: relative;
            }

            th.numeric .cell {
                flex-flow: row;
                margin: auto;
                width: fit-content;
            }

            table.tabular th {
                padding-top: px-to-rem(18);
                font-size: $font-size-sm;
            }

            td {
                margin: px-to-rem(10);
                vertical-align: middle;

                &.numeric .cell {
                    width: fit-content;
                    margin: auto;
                }

                &:first-of-type {
                    .cell {
                        @include ellipsis-multiline(2);
                    }
                }

                &:last-of-type {
                    padding: 0;
                }
            }
        }
    }
    .site-table-wrapper .placeholder .animated-background {
        .row.divider {
            padding-top: 25px;
            padding-bottom: 25px;
        }
        .row:nth-child(2) {
            padding-top: 0px;
        }
        .row:nth-last-child(2) {
            padding-bottom: 0px;
        }
    }
</style>
